import {
  type Except,
  type HasRequiredKeys,
  notEmpty,
  type ValidatedKey,
} from '@festina-life/core'
import cx from 'classnames'
import { FieldArray, useField } from 'formik'
import get from 'lodash-es/get'
import {
  type FC,
  type ReactNode,
  useEffect,
  useEffectEvent,
  useState,
} from 'react'

import { Button } from '../../actions/button/Button'
import { FieldSet } from '../../forms/fieldSet/FieldSet'
import { InputWrapper } from '../../forms/inputWrapper/InputWrapper'
import { inputMinWidthStyles } from '../../forms/inputWrapper/inputWrapperStyles'
import { useFormikContext } from '../../hooks/useFormikContext'
import { useFormikErrorWithState } from '../../hooks/useFormikErrorWithState'
import { useFormikFieldArrayItemError } from '../../hooks/useFormikFieldArrayItemError'
import { useFormikIsDisabled } from '../../hooks/useFormikIsDisabled'
import { useFormikIsRequired } from '../../hooks/useFormikIsRequired'
import { useTranslate } from '../../hooks/useTranslate'
import { Stack } from '../../structure/stack/Stack'

type Props<
  T extends object,
  P extends Omit<Record<string, unknown>, 'fieldNamePrefix'> =
    NonNullable<unknown>,
> = {
  label?: string
  isFullWidth?: boolean
  name: ValidatedKey
  elementLabel: string
  valuesToSummary?: (values?: NoInfer<Partial<T>>) => ReactNode[]
  elementInitializer: (values?: Array<T>) => T
  ChildElement: FC<{ fieldNamePrefix: ValidatedKey; index: number } & P>
  canCopyTemplate?: boolean
  shouldPreventDeletingLastItem?: boolean
} & (HasRequiredKeys<Except<P, 'fieldNamePrefix'>> extends true
  ? { childProps: P }
  : { childProps?: undefined })

export const FormikFieldArray = <
  T extends object,
  P extends Record<string, unknown> = NonNullable<unknown>,
>({
  name,
  elementLabel,
  label,
  valuesToSummary,
  elementInitializer,
  ChildElement,
  canCopyTemplate,
  childProps,
  isFullWidth = false,
  shouldPreventDeletingLastItem = false,
}: Props<T, P>) => {
  const translate = useTranslate()
  const { status, initialValues } = useFormikContext()

  const [field, meta] = useField<T[] | undefined>(name)
  const isFieldDisabled = useFormikIsDisabled(false)
  const isRequired = useFormikIsRequired(name, undefined)
  const { error } = useFormikErrorWithState(meta)
  const hasItemError = useFormikFieldArrayItemError(name)
  const arrayValues = field.value
  const filteredArrayValues = arrayValues?.filter(notEmpty)
  const hasValues =
    arrayValues && filteredArrayValues && filteredArrayValues.length > 0
  const isDiffing = status.shouldShowChangesInForm
  const [openIndex, setOpenIndex] = useState<number | null>(
    shouldPreventDeletingLastItem &&
      hasValues &&
      filteredArrayValues.length === 1
      ? 0
      : null,
  )
  const [changedIndices, setChangedIndices] = useState<number[] | null>(null)
  const [shouldAnimateFieldset, setShouldAnimateFieldset] = useState(false)

  const handleShouldSetChanges = useEffectEvent(() => {
    const initialArrayValues = get(initialValues, name) as T[] | undefined
    setChangedIndices(
      arrayValues?.flatMap((item, index) =>
        item &&
        JSON.stringify(item) !== JSON.stringify(initialArrayValues?.[index])
          ? [index]
          : [],
      ) ?? [],
    )
  })

  useEffect(() => {
    if (!shouldAnimateFieldset) {
      // Enable animations after the initial mount to avoid animating all fieldsets when the component is first rendered
      setShouldAnimateFieldset(true)
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isDiffing) {
      handleShouldSetChanges()
    } else {
      setChangedIndices(null)
    }
  }, [isDiffing])

  return isDiffing && changedIndices?.length === 0 ? null : (
    <InputWrapper
      label={label}
      hasCustomWidth
      className={cx(
        isFullWidth
          ? 'w-full'
          : 'w-auto min-w-(--amaze-inputWrapper-width) self-start',
      )}
      isRequired={isRequired}
      error={typeof error === 'string' ? error : undefined} // Error can also be an object. Only pass through a string error for the array itself.
    >
      <FieldArray
        name={name}
        render={({ replace, push, insert }) => (
          <Stack gap="condensed" className="flex-col">
            {hasValues &&
              arrayValues.map((item, index) => {
                const summaryValues =
                  valuesToSummary?.(item).filter(notEmpty) ?? []
                const isItemInError = hasItemError(index)

                // Don't render empty items, but conserve their positions in the field array.
                return !item ||
                  (isDiffing &&
                    changedIndices &&
                    !changedIndices.includes(index)) ? null : (
                  <FieldSet
                    // oxlint-disable-next-line react/no-array-index-key -- we don't have a unique key
                    key={index}
                    disabled={isFieldDisabled}
                    isInError={isItemInError}
                    isOpen={openIndex === index}
                    setIsOpen={(open) =>
                      setOpenIndex(
                        open ? index : (prev) => (prev === index ? null : prev),
                      )
                    }
                    shouldAnimateAndFocusOnMount={shouldAnimateFieldset}
                    label={elementLabel}
                    labelExtras={
                      summaryValues.length > 0
                        ? summaryValues
                        : [`${filteredArrayValues.indexOf(item) + 1}`]
                    }
                    className={cx(isFullWidth && 'w-full!')}
                    actions={[
                      ...(shouldPreventDeletingLastItem &&
                      filteredArrayValues.length === 1
                        ? []
                        : [
                            {
                              onClick: () => {
                                // Preserve field indices while editing; FormikForm compacts empty slots on submit.
                                replace(index, undefined)
                                if (openIndex === index) {
                                  setOpenIndex(null)
                                }
                              },
                              disabled: isFieldDisabled,
                              iconName: 'Trash' as const,
                              key: 'delete',
                              isDanger: true,
                            },
                          ]),
                      ...(canCopyTemplate
                        ? [
                            {
                              onClick: () =>
                                insert(index + 1, {
                                  ...arrayValues[index],
                                }),
                              disabled: isFieldDisabled,
                              iconName: 'DocumentDuplicate' as const,
                              key: 'copy',
                            },
                          ]
                        : []),
                    ]}
                  >
                    <ChildElement
                      fieldNamePrefix={`${name}.${index}` as ValidatedKey}
                      index={index}
                      {...((childProps ?? {}) as P)}
                    />
                  </FieldSet>
                )
              })}
            {!isFieldDisabled && (
              <Button
                variant="secondary"
                isFullWidth
                onClick={() => {
                  setOpenIndex(arrayValues?.length ?? 0)
                  return push(elementInitializer(arrayValues))
                }}
                startIcon="Plus"
                className={cx('justify-start!', inputMinWidthStyles)}
              >
                {hasValues
                  ? translate('entries.add.another', { entry: elementLabel })
                  : translate('entries.add', { entry: elementLabel })}
              </Button>
            )}
          </Stack>
        )}
      />
    </InputWrapper>
  )
}
